import React, {Fragment, useEffect, useState} from 'react';

import {
	Autocomplete,
	Box,
	capitalize,
	Divider,
	FormControl,
	Radio,
	Skeleton,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import {GetNeighborhoodsDentalGuideParams, TypeLocalNameEnum} from 'domain/usecases';
import {GetExamsDistrictsParams} from 'domain/usecases/private/booking-flow/exams/get-exams-districts';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {Drawer} from 'presentation/components/Drawer';
import {OdontoGuideServicesTypeEnum} from 'presentation/constants';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {FlowEnum} from 'presentation/pages/private/booking/constants';
import {useGetExamsDistrictsQuery} from 'presentation/pages/private/booking/queries/useGetExamsDistrictsQuery';

import {useGetOdontoDistrictsQuery} from 'presentation/pages/private/booking/queries/useGetOdontoDistrictsQuery';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {StyledBox} from './styles';
import {GeolocationFilterProps} from './types';
import {DistanceSlider} from '../../../DistanceSlider';
import {useGetHealthDistrictsQuery} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthDistrictSelection/queries/useGetHealthDistrictsQuery';

const {
	referencePoint,
	maxDistance,
	referencePointMaxDistance,
	changeLocation,
	specificDistrict,
	selectDistrict,
	selectDistrictAlert,
	selectSpecificDistrict,
	fromYourLocation,
} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function GeolocationFilter({
	openDrawerState,
	distanceFilterAtMaxDistance,
	handleCloseDrawer,
	handleConfirmDistance,
	handleClearDistance,
	districtGeolocationState,
	setDistrictGeolocationState,
	selectedFilterGeolocationState,
	setSelectedFilterGeolocationState,
	flowType,
	servingPeopleWithDisabilities,
	handleClearDisability,
	handleConfirmDisability,
}: GeolocationFilterProps) {
	const {palette} = useTheme();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const isHealth = flowType === FlowEnum.HEALTH;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const [districtErrorState, setDistrictErrorState] = useState(false);
	const {data: healthCard, isLoading: healthCardIsLoading} = useGetHealthCardQuery();
	const cdPlanoANS =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns || '';

	const healthDistrictParams: GetHealthDistrictsParams = {
		stateCode: bookingData.ufCode?.toUpperCase(),
		locationName: bookingData.cityName?.toUpperCase(),
		locationType: TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(bookingData.specialtyCode),
	};

	const examDistrictParams: GetExamsDistrictsParams = {
		cdUf: bookingData.ufCode?.toUpperCase(),
		nmLocal: bookingData.cityName?.toUpperCase(),
		nmTipoLocal: TypeLocalNameEnum.CIDADE,
		cdTipoExame: bookingData.specialtyCode,
	};

	const districtParams: GetNeighborhoodsDentalGuideParams = {
		cdEspecialidade: String(bookingData.specialtyCode),
		nmCidade: bookingData.cityName,
		cdUf: bookingData.ufCode,
		cdPlanoANS,
		dsServico: OdontoGuideServicesTypeEnum.OUTPATIENT,
		tpLocalidade: capitalize(TypeLocalNameEnum.CIDADE.toLowerCase()),
	};

	const {data: healthDistricts, isFetching: isHealthDistrictsLoading} =
		useGetHealthDistrictsQuery({
			params: healthDistrictParams,
			enabled: flowType === FlowEnum.HEALTH,
		});

	const {data: examsDistricts, isFetching: isExamsDistrictsLoading} = useGetExamsDistrictsQuery(
		examDistrictParams,
		flowType === FlowEnum.EXAM,
	);

	const {isFetching: odontoDistrictListIsLoading, data: odontoDistrictList} =
		useGetOdontoDistrictsQuery({params: districtParams, enabled: isOdontoFlow});

	const districtList = () => {
		if (isHealth) {
			return healthDistricts;
		}

		if (isOdontoFlow) {
			return odontoDistrictList;
		}
		return examsDistricts;
	};

	const isDistrictListLoading = () => {
		if (isHealth) {
			return isHealthDistrictsLoading;
		}

		if (isOdontoFlow) {
			return odontoDistrictListIsLoading || healthCardIsLoading;
		}

		return isExamsDistrictsLoading;
	};
	const isDistrictListEmpty =
		!isDistrictListLoading() && (!districtList() || districtList()?.length === 0);

	const citiesNames =
		districtList()?.find(
			item => item.district.toLowerCase() === districtGeolocationState.toLowerCase(),
		)?.city || '';

	const distanceFilterTitle = (
		<Fragment>
			{distanceFilterAtMaxDistance ? referencePointMaxDistance : referencePoint}{' '}
			<Typography color="primary.dark" component="span" fontWeight={700}>
				{fromYourLocation}
			</Typography>
			. {distanceFilterAtMaxDistance ? changeLocation : ''}
		</Fragment>
	);

	const handleChange = (value: string) => {
		setDistrictGeolocationState(value);
		setDistrictErrorState(false);
	};

	const handleFilterChange = (filterType: 'distance' | 'district') => {
		setSelectedFilterGeolocationState(filterType);

		if (filterType === 'distance') {
			setDistrictErrorState(false);
		}
	};

	const handleFilterGeolocationConfirm = () => {
		if (selectedFilterGeolocationState === 'district') {
			if (districtGeolocationState === '') {
				setDistrictErrorState(true);
				return;
			}
			setDistrictErrorState(false);
			setBookingData(prev => ({
				...prev,
				hasGeoDistrictsFilter: true,
				cityName: bookingData.cityName?.toUpperCase(),
				ufCode: bookingData.ufCode?.toUpperCase(),
				districtName: districtGeolocationState,
				citiesNames,
			}));
			if (isOdontoFlow && handleConfirmDisability) {
				handleConfirmDisability();
			}
			handleClearDistance();
			handleCloseDrawer();
			return;
		}

		if (isOdontoFlow && handleConfirmDisability) {
			handleConfirmDisability();
		}
		handleConfirmDistance();
		setBookingData(prev => ({
			...prev,
			districtName: '',
			citiesNames: '',
			hasGeoDistrictsFilter: false,
		}));
		handleCloseDrawer();
		setDistrictGeolocationState('');
		setDistrictErrorState(false);
	};

	const handleFilterGeolocationCancel = () => {
		setSelectedFilterGeolocationState('distance');
		handleClearDistance();
		if (isOdontoFlow && handleClearDisability) {
			handleClearDisability();
		}
		setBookingData(prev => ({
			...prev,
			hasGeoDistrictsFilter: false,
			districtName: '',
			citiesNames: '',
		}));
		setDistrictGeolocationState('');
		handleCloseDrawer();
		setDistrictErrorState(false);
	};

	const districtBorderColor = () => {
		if (districtErrorState) return palette.error.main;
		if (selectedFilterGeolocationState === 'district') {
			return palette.primary.main;
		}

		return palette.divider;
	};

	useEffect(() => {
		if (!openDrawerState) {
			setDistrictErrorState(false);
		}
	}, [openDrawerState]);

	return (
		<Fragment>
			{isOdontoFlow && servingPeopleWithDisabilities ? (
				<Stack mb={1}>
					<Drawer.ContentListByCategorySwitch
						categoryList={[servingPeopleWithDisabilities]}
					/>
					<StyledBox>
						<Divider />
					</StyledBox>
				</Stack>
			) : null}
			<Drawer.Content sx={{overflow: 'visible'}}>
				<Stack gap={2}>
					<Box
						p={2}
						borderRadius={1}
						onClick={() => handleFilterChange('distance')}
						sx={{
							border: `1px solid ${selectedFilterGeolocationState === 'distance' ? palette.primary.main : palette.divider}`,
						}}>
						<Stack gap={'5px'}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between">
								<Typography
									variant="subtitle1"
									fontWeight={700}
									color="text.secondary">
									{maxDistance}
								</Typography>
								<Radio
									checked={selectedFilterGeolocationState === 'distance'}
									onChange={() => handleFilterChange('distance')}
									color="primary"
									sx={{p: 0, mr: 1}}
								/>
							</Stack>
							<Typography variant="body2" color="text.secondary">
								{distanceFilterTitle}
							</Typography>
							<Box px={1}>
								<DistanceSlider drawerIsOpen={openDrawerState} isOdonto />
							</Box>
						</Stack>
					</Box>
					<Stack gap={0.5}>
						<Box
							p={2}
							borderRadius={1}
							onClick={
								isDistrictListEmpty || isDistrictListLoading()
									? undefined
									: () => handleFilterChange('district')
							}
							sx={{
								border: `1px solid ${districtBorderColor()}`,
							}}>
							<Stack gap={'5px'}>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="space-between">
									<Typography
										variant="subtitle1"
										fontWeight={700}
										color="text.secondary">
										{specificDistrict}
									</Typography>
									<Radio
										checked={selectedFilterGeolocationState === 'district'}
										onChange={() => handleFilterChange('district')}
										color="primary"
										disabled={isDistrictListEmpty || isDistrictListLoading()}
										sx={{p: 0, mr: 1}}
									/>
								</Stack>
								<Stack gap={1}>
									<Typography variant="body2" color="text.secondary">
										{selectSpecificDistrict}
									</Typography>

									{isDistrictListLoading() ? (
										<Skeleton height={'56px'} variant="rectangular" />
									) : (
										<FormControl fullWidth>
											<Autocomplete
												value={districtGeolocationState}
												onChange={(event, newValue) => {
													if (newValue) handleChange(newValue);
												}}
												options={
													districtList()?.map(d =>
														formatText(d?.district),
													) || []
												}
												disabled={isDistrictListEmpty}
												disablePortal
												freeSolo={false}
												disableClearable
												renderInput={params => (
													<TextField
														{...params}
														placeholder={selectDistrict}
														variant="outlined"
														sx={{
															'& .MuiInputBase-input::placeholder': {
																opacity: 1,
															},
														}}
														slotProps={{
															htmlInput: {
																...params.inputProps,
																readOnly: true,
															},
														}}
													/>
												)}
												getOptionLabel={option => option || ''}
												isOptionEqualToValue={(option, value) =>
													option === value
												}
											/>
										</FormControl>
									)}
								</Stack>
							</Stack>
						</Box>
						{districtErrorState ? (
							<Typography variant="caption" color="error">
								{selectDistrictAlert}
							</Typography>
						) : null}
					</Stack>
				</Stack>
			</Drawer.Content>
			<Drawer.Footer
				firstButtonOnClick={handleFilterGeolocationCancel}
				secondButtonOnClick={handleFilterGeolocationConfirm}
			/>
		</Fragment>
	);
}
